import {
  ColumnPinningState,
  ColumnSizingState,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
  TableState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from 'nuqs';
import { useCallback, useMemo, useState } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { logger } from '../utils/logger';

type GraphQLQueryHook<TQueryResult, TVariables = unknown> = (options: {
  variables: TVariables;
  fetchPolicy: 'cache-and-network';
}) => {
  data?: TQueryResult;
  loading: boolean;
  error?: Error;
  refetch: () => Promise<unknown>;
};

type ColumnSettings = Pick<
  TableState,
  'columnVisibility' | 'columnOrder' | 'columnPinning'
>;

interface BaseUrlState {
  page: number;
  pageSize: number;
  sortBy: string | null;
  sortOrder: 'asc' | 'desc' | null;
}

export interface UseTableStateConfig<
  TData,
  TFilters,
  TQueryResult,
  TVariables = unknown,
> {
  useQuery: GraphQLQueryHook<TQueryResult, TVariables>;
  dataAccessor: (data: TQueryResult | undefined) => TData[] | undefined;
  /**
   * When provided, filters are parsed from the URL and `buildFilter` constructs the GraphQL filter.
   * When omitted, pass `graphQLFilter` and `externalFilters` to manage filters elsewhere.
   */
  filterParsers?: Record<keyof TFilters, unknown>;
  buildFilter?: (filters: TFilters) => unknown;
  graphQLFilter?: unknown;
  externalFilters?: TFilters;
  buildOrderBy: (sorting: SortingState) => unknown;
  localStoragePrefix: string;
  defaultPageSize?: number;
  /** Default column visibility state. Columns not in this map default to visible. */
  defaultColumnVisibility?: VisibilityState;
  /**
   * Request the whole list in one page. A caller that folds rows together
   * needs every row, because a server page can split the rows it folds. That
   * caller paginates the folded rows itself.
   */
  fetchAllRows?: boolean;
}

export interface UseTableStateReturn<TData, TFilters, TQueryResult = unknown> {
  data: TData[];
  rawData?: TQueryResult;
  loading: boolean;
  error?: Error;
  refetch: () => Promise<unknown>;
  pagination: PaginationState;
  sorting: SortingState;
  columnVisibility: VisibilityState;
  columnOrder: string[];
  columnPinning: ColumnPinningState;
  columnSizing: ColumnSizingState;
  rowSelection: RowSelectionState;
  onPaginationChange: OnChangeFn<PaginationState>;
  onSortingChange: OnChangeFn<SortingState>;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  onColumnOrderChange: OnChangeFn<string[]>;
  onColumnPinningChange: OnChangeFn<ColumnPinningState>;
  onColumnSizingChange: OnChangeFn<ColumnSizingState>;
  resetColumnSizing: () => void;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  filters: TFilters;
  setFilters: (filters: Partial<TFilters>) => void;
}

/** Page size that stands in for "no pagination" (see `fetchAllRows`). */
const ALL_ROWS_LIMIT = 10000;

const createDefaultColumnSettings = (
  defaultColumnVisibility: VisibilityState = {}
): ColumnSettings => ({
  columnVisibility: defaultColumnVisibility,
  columnOrder: [],
  columnPinning: { left: ['select'], right: ['actions'] },
});

const convertUrlToSorting = (
  sortBy: string | null,
  sortOrder: 'asc' | 'desc' | null
): SortingState => {
  if (!sortBy || !sortOrder) return [];
  return [{ id: sortBy, desc: sortOrder === 'desc' }];
};

const convertSelectionToRowState = (selectedIds: string[]): RowSelectionState =>
  selectedIds.reduce(
    (acc, id) => ({ ...acc, [id]: true }),
    {} as RowSelectionState
  );

const convertRowStateToSelection = (
  rowSelection: RowSelectionState
): string[] => Object.keys(rowSelection).filter((id) => rowSelection[id]);

export function useTableState<
  TData,
  TFilters,
  TQueryResult,
  TVariables = unknown,
>(
  config: UseTableStateConfig<TData, TFilters, TQueryResult, TVariables>
): UseTableStateReturn<TData, TFilters, TQueryResult> {
  const [columnSettings, setColumnSettings] = useLocalStorage(
    `tf-pulse-${config.localStoragePrefix}-table-settings`,
    createDefaultColumnSettings(config.defaultColumnVisibility)
  );

  const [urlState, setUrlState] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    pageSize: parseAsInteger.withDefault(config.defaultPageSize ?? 20),
    sortBy: parseAsString,
    sortOrder: parseAsStringEnum<'asc' | 'desc'>(['asc', 'desc']),
    ...(config.filterParsers ?? {}),
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Column sizing is kept in local state (not persisted to localStorage)
  // so columns are sized proportionally on each page load
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  const baseUrlState = urlState as BaseUrlState & Partial<TFilters>;
  const sorting = convertUrlToSorting(
    baseUrlState.sortBy,
    baseUrlState.sortOrder
  );

  const graphQLFilter = config.buildFilter
    ? config.buildFilter(baseUrlState as TFilters)
    : config.graphQLFilter;
  const graphQLOrderBy = config.buildOrderBy(sorting);

  const { data, loading, error, refetch } = config.useQuery({
    variables: {
      pagination: config.fetchAllRows
        ? { offset: 0, limit: ALL_ROWS_LIMIT }
        : {
            offset: (baseUrlState.page - 1) * baseUrlState.pageSize,
            limit: baseUrlState.pageSize,
          },
      orderBy: graphQLOrderBy,
      filter: graphQLFilter,
    } as TVariables,
    fetchPolicy: 'cache-and-network',
  });

  const items = config.dataAccessor(data) ?? [];

  const handlePaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updater) => {
      const currentPagination: PaginationState = {
        pageIndex: baseUrlState.page - 1,
        pageSize: baseUrlState.pageSize,
      };
      const newPagination =
        typeof updater === 'function' ? updater(currentPagination) : updater;

      setUrlState((prev) => ({
        ...prev,
        page: newPagination.pageIndex + 1,
        pageSize: newPagination.pageSize,
      }));
    },
    [baseUrlState.page, baseUrlState.pageSize, setUrlState]
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const newSorting =
        typeof updater === 'function' ? updater(sorting) : updater;
      const firstSort = newSorting[0];

      setUrlState((prev) => ({
        ...prev,
        sortBy: firstSort?.id ?? null,
        sortOrder: firstSort?.desc ? 'desc' : 'asc',
      }));
    },
    [sorting, setUrlState]
  );

  const handleColumnVisibilityChange: OnChangeFn<VisibilityState> = useCallback(
    (updater) => {
      setColumnSettings((prev) => {
        const newVisibility =
          typeof updater === 'function'
            ? updater(prev.columnVisibility)
            : updater;
        logger.debug('[useTableState] Updating column visibility:', {
          prev: prev.columnVisibility,
          new: newVisibility,
        });
        return {
          ...prev,
          columnVisibility: newVisibility,
        };
      });
    },
    [setColumnSettings]
  );

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
    (updater) => {
      setSelectedIds((prev) => {
        const currentSelection = convertSelectionToRowState(prev);
        const newSelection =
          typeof updater === 'function' ? updater(currentSelection) : updater;
        return convertRowStateToSelection(newSelection);
      });
    },
    [setSelectedIds]
  );

  const handleColumnOrderChange: OnChangeFn<string[]> = useCallback(
    (updater) => {
      setColumnSettings((prev) => {
        const newOrder =
          typeof updater === 'function' ? updater(prev.columnOrder) : updater;
        logger.debug('[useTableState] Updating column order:', {
          prev: prev.columnOrder,
          new: newOrder,
        });
        return { ...prev, columnOrder: newOrder };
      });
    },
    [setColumnSettings]
  );

  const handleColumnPinningChange: OnChangeFn<ColumnPinningState> = useCallback(
    (updater) => {
      setColumnSettings((prev) => {
        const newPinning =
          typeof updater === 'function' ? updater(prev.columnPinning) : updater;
        return { ...prev, columnPinning: newPinning };
      });
    },
    [setColumnSettings]
  );

  const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = useCallback(
    (updater) => {
      setColumnSizing((prev) => {
        return typeof updater === 'function' ? updater(prev) : updater;
      });
    },
    []
  );

  // A stable object per change, so consumers that memoize on it do not
  // recompute on every render
  const columnVisibility = useMemo<VisibilityState>(
    () => ({
      ...config.defaultColumnVisibility,
      ...columnSettings.columnVisibility,
    }),
    [config.defaultColumnVisibility, columnSettings.columnVisibility]
  );

  const resetColumnSizing = useCallback(() => {
    setColumnSizing({});
  }, []);

  const handleFiltersChange = useCallback(
    (filters: Partial<TFilters>) => {
      setUrlState((prev) => ({
        ...prev,
        ...filters,
      }));
    },
    [setUrlState]
  );

  return {
    data: items,
    rawData: data,
    loading,
    error,
    refetch,
    pagination: {
      pageIndex: baseUrlState.page - 1,
      pageSize: baseUrlState.pageSize,
    },
    sorting,
    columnVisibility,
    columnOrder: columnSettings.columnOrder,
    columnPinning: columnSettings.columnPinning,
    columnSizing,
    rowSelection: convertSelectionToRowState(selectedIds),
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnOrderChange: handleColumnOrderChange,
    onColumnPinningChange: handleColumnPinningChange,
    onColumnSizingChange: handleColumnSizingChange,
    resetColumnSizing,
    onRowSelectionChange: handleRowSelectionChange,
    filters: (config.externalFilters ?? baseUrlState) as TFilters,
    setFilters: config.filterParsers ? handleFiltersChange : () => undefined,
  };
}
